/**
 * Texting a customer.
 *
 * The rules that decide whether a message can be sent live in the database —
 * the opt-out list and the blacklist are both there, and `may_send_text` is the
 * one answer both the screen and the Edge Function ask for. What is here is
 * everything a screen needs in order to be honest about a message *before* and
 * *after* that: how long it may be, and what Twilio's refusal actually meant.
 *
 * The second one matters more than it looks. A text that silently never arrives
 * is the worst outcome in this whole feature — the customer thinks they were
 * ignored, and the rep thinks they answered. Twilio always says why, in a
 * number; this turns the number into the sentence a roofer can act on.
 */

/** Twilio refuses anything longer, and a message that long is an email. The
 * Edge Function checks this too — this copy is so the box can say so while
 * somebody is still typing. */
export const MAX_TEXT_LENGTH = 1600;

/** A single SMS segment of plain characters. Past this, carriers split the
 * message and charge per piece. */
export const SEGMENT_LENGTH = 160;
/** A split message loses seven characters per piece to the header that
 * reassembles it. */
export const SEGMENT_LENGTH_SPLIT = 153;

/**
 * Characters outside this set turn an SMS into a UCS-2 one, which fits 70 to a
 * segment rather than 160. In practice: emoji, curly quotes, and the ellipsis
 * that a phone keyboard substitutes for three dots.
 */
const GSM_7 =
  /^[A-Za-z0-9@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ!"#¤%&'()*+,\-./:;<=>?¡ÄÖÑÜ§¿äöñüà €^{}[~\]|\n\r\f]*$/;

export interface TextCost {
  characters: number;
  segments: number;
  /** True when one emoji has quietly tripled the price of the message. */
  unicode: boolean;
}

/**
 * What this message will actually cost to send.
 *
 * Shown next to the box, because the surprise is real: a 90-character reply
 * with a thumbs-up in it is two segments, and a 200-character one is two
 * whether or not anybody meant it to be.
 */
export function textCost(body: string): TextCost {
  const characters = body.length;
  const unicode = !GSM_7.test(body);
  const single = unicode ? 70 : SEGMENT_LENGTH;
  const split = unicode ? 67 : SEGMENT_LENGTH_SPLIT;

  if (characters === 0) return { characters: 0, segments: 0, unicode };
  if (characters <= single) return { characters, segments: 1, unicode };
  return { characters, segments: Math.ceil(characters / split), unicode };
}

/**
 * What Twilio's refusal meant, in words.
 *
 * Only the codes this company can actually hit are named. Anything else keeps
 * the number, because "error 30034" is at least searchable — inventing a
 * reassuring sentence for a code nobody has seen would be worse than admitting
 * we do not know.
 */
export function describeTextFailure(
  code: number | null | undefined,
  fallback?: string | null,
): string {
  switch (code) {
    case 21610:
      return "They replied STOP, so this number cannot be texted again unless they text START.";
    case 21211:
    case 21614:
      return "That is not a number that can receive a text — it may be a landline.";
    case 21612:
      return "Twilio would not carry a message to that number.";
    case 30003:
      return "Their phone was unreachable — switched off, or out of coverage.";
    case 30004:
      return "Their phone blocked the message.";
    case 30005:
      return "That number does not exist any more.";
    case 30006:
      return "That is a landline, or a carrier that does not take texts.";
    case 30007:
      return "Their carrier filtered the message as spam. A shorter message without links usually gets through.";
    case 30008:
      return "The carrier would not say why it failed. Try once more, then ring them.";
    // No code at all falls through to the same words as an unnamed one.
    case null:
    case undefined:
    default:
      return fallback?.trim()
        ? fallback.trim()
        : code
          ? `The carrier refused it (error ${code}).`
          : "The message did not arrive.";
  }
}

/** Twilio's statuses, as the four things a person actually needs to tell
 * apart. */
export type TextState = "sending" | "sent" | "failed" | "received";

export function textState(message: {
  direction: string;
  status: string;
}): TextState {
  if (message.direction === "inbound") return "received";
  if (message.status === "undelivered" || message.status === "failed") {
    return "failed";
  }
  if (message.status === "delivered" || message.status === "sent")
    return "sent";
  return "sending";
}

/**
 * A day's worth of messages, for a conversation that reads top to bottom.
 *
 * Grouped by the local day rather than by the ISO date, so that a text at nine
 * at night is filed under the day the person remembers sending it.
 */
export interface TextDay<T> {
  /** `YYYY-MM-DD` in local time — a key, not something to print. */
  day: string;
  messages: T[];
}

export function byDay<T extends { sent_at: string }>(
  messages: readonly T[],
): TextDay<T>[] {
  const days = new Map<string, T[]>();

  for (const message of [...messages].sort(
    (a, b) => Date.parse(a.sent_at) - Date.parse(b.sent_at),
  )) {
    const at = new Date(message.sent_at);
    if (Number.isNaN(at.getTime())) continue;
    const key = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}-${String(
      at.getDate(),
    ).padStart(2, "0")}`;
    const existing = days.get(key);
    if (existing) existing.push(message);
    else days.set(key, [message]);
  }

  return [...days.entries()].map(([day, list]) => ({ day, messages: list }));
}
